import { Navigate, useNavigate } from 'react-router-dom';
import MetrcImportDialog from '../../MetrcImportDialog';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useSsoReauth } from '../../contexts/SsoReauthContext';
import { getFacilities } from '../../hooks/useFacilities';
import { importFacility } from '../../hooks/useMetrcImports';
import { useImportsWizard } from './ImportsWizardContext';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import {
  getEndpointErrorMessage,
  getErrorBody,
  getErrorStatus,
  isSsoReauthRequired,
} from '../../utils/requestErrors';
import { getAvailableActions, getWriteActions } from '../../utils/permissionCatalog';
import { checkPermissionActions } from '../../utils/permissions';

const facilityImportErrorFallback = 'Unable to import facilities. Please try again.';

function getFacilityImportErrorMessage(error: unknown): string {
  return getEndpointErrorMessage({
    status: getErrorStatus(error),
    body: getErrorBody(error),
  }, facilityImportErrorFallback);
}

export default function MetrcSelectContainer() {
  const navigate = useNavigate();
  const { org, currentUser } = useLegacyAppSession();
  const { handleSsoError } = useSsoReauth();
  const {
    metrcAuthData,
    resetMetrcWizard,
    returnTo,
    setMetrcFacilitiesSelected,
    workflow,
  } = useImportsWizard();
  const actions = getAvailableActions(currentUser);
  const canImportFacilities =
    checkPermissionActions(actions, getWriteActions('Facilities')) &&
    checkPermissionActions(actions, getWriteActions('Imports'));

  if (!metrcAuthData) {
    return <Navigate to="/imports/metrc/auth" replace />;
  }

  return (
    <MetrcImportDialog
      organizationId={Number(org?.id) || undefined}
      marketId={metrcAuthData.marketId}
      market={metrcAuthData.market}
      username={metrcAuthData.username}
      restrictToImportedFacilities={workflow === 'products'}
      // Only the product workflow chooses already-imported facilities; New Import's product step is the next step for a newly imported one.
      excludeImportedFacilities={workflow !== 'products'}
      onClose={() => {
        resetMetrcWizard();
        navigate(returnTo, { replace: true });
      }}
      onSave={async ({ facilities }) => {
        if (!org?.id) return;

        let facilityImportsStarted = false;
        try {
          const response = await getFacilities(org.id, metrcAuthData.marketId, 0, 500).catch(() => []);
          const existingFacilities = (response || []).map((facility) => ({
            metrcFacilityId: facility?.metrcFacilityId,
            id: facility?.id,
          }));

          const selectedFacilities = facilities.map((facility) => ({
            ...facility,
            dbFacilityId: facility.dbFacilityId
              ?? existingFacilities.find((existing) => facility.id == existing.metrcFacilityId)?.id
              ?? null,
          }));

          const missingFacilities = selectedFacilities.filter((facility) => !facility.dbFacilityId);
          let facilitiesWithDbIds = selectedFacilities;

          if (missingFacilities.length > 0 && workflow === 'products') {
            throw new Error('Selected facilities must be imported before products can be imported.');
          }

          if (missingFacilities.length > 0 && !canImportFacilities) {
            throw new Error('You do not have permission to import facilities.');
          }

          if (missingFacilities.length > 0) {
            facilityImportsStarted = true;
            const results = await Promise.all(
              missingFacilities.map((facility) =>
                importFacility({
                  organizationId: org.id,
                  marketId: metrcAuthData.marketId,
                  metrcFacilityId: facility.id,
                })
              )
            );

            const importedByMetrcId = new Map<number, number>();
            (results || []).forEach((result) => {
              const metrcFacilityId = Number(result?.metrcFacilityId);
              const dbFacilityId = Number(result?.id);
              if (Number.isFinite(metrcFacilityId) && Number.isFinite(dbFacilityId)) {
                importedByMetrcId.set(metrcFacilityId, dbFacilityId);
              }
            });
            facilitiesWithDbIds = selectedFacilities.map((facility) => ({
              ...facility,
              dbFacilityId: facility.dbFacilityId
                ?? importedByMetrcId.get(Number(facility.id))
                ?? null,
            }));

            if (facilitiesWithDbIds.some((facility) => !facility.dbFacilityId)) {
              throw new Error('One or more imported facilities did not return a RID facility ID.');
            }
          }

          showToastSuccess(
            missingFacilities.length > 0
              ? 'Facilities successfully imported'
              : 'Facilities successfully selected',
          );
          if (workflow === 'facilities') {
            resetMetrcWizard();
            navigate(returnTo, { replace: true });
            return;
          }

          setMetrcFacilitiesSelected(facilitiesWithDbIds);
          navigate('/imports/metrc/products');
        } catch (error) {
          console.error(error);
          // The wizard state does not survive the sign-in redirect, so the user comes back to where the import started.
          if (handleSsoError(error, { returnTo })) return;
          const message = getFacilityImportErrorMessage(error);
          showToastError(facilityImportsStarted
            ? `${message} Some facilities may have imported successfully.`
            : message);
          if (isSsoReauthRequired(error)) {
            // No stored Metrc sign-in works any more; the auth screen offers the sign-in again.
            navigate('/imports/metrc/auth', { replace: true, state: { returnTo, workflow } });
          }
        }
      }}
    />
  );
}
